import { useState } from 'react'
import { useMutation, type QueryClient } from '@tanstack/react-query'
import type {
  ComplianceExceptionDTO,
  ExceptionReason,
} from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Label } from '@deepguide-ai/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@deepguide-ai/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Loader2, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { ExceptionKindSelect } from './ExceptionKindSelect'
import {
  ExceptionExpiryField,
  useExceptionExpiry,
} from './ExceptionExpiryField'
import {
  FINDING_REASONS,
  KIND_POPOVER_HINTS,
  REASON_LABELS,
  SCOPE_REASONS,
  SCOPE_REMOVE_LABEL,
  SCOPE_REMOVE_TITLE,
  SCOPE_REMOVED_TOAST,
  defaultReasonForKind,
  type ResourceExceptionKind,
} from './findings-exception-helpers'

export function invalidateResourceScopeQueries(queryClient: QueryClient): void {
  for (const queryKey of [
    trpc.checker.getCheckRunDetail.queryKey(),
    trpc.checker.listCheckRunHistory.queryKey(),
    trpc.checker.listProviderChecks.queryKey(),
    trpc.checker.listServiceResources.queryKey(),
    trpc.complianceException.list.queryKey(),
    trpc.guidedTemplate.getLatestApiTestRun.queryKey(),
    trpc.guidedTemplate.listApiTestRuns.queryKey(),
    trpc.guidedTemplate.listAllTests.pathKey(),
    trpc.compliance.getControlsTree.queryKey(),
    trpc.guidedTemplate.getStatusById.queryKey(),
    trpc.guidedTemplate.getLiveAnchorRollup.queryKey(),
  ]) {
    void queryClient.invalidateQueries({ queryKey })
  }
}

function useResourceScopeMutation() {
  return useMutation(trpc.complianceException.setResourceScope.mutationOptions())
}

export function useCheckResourceScopeMutation() {
  return useMutation(
    trpc.complianceException.setCheckResourceScope.mutationOptions()
  )
}

export function ResourceScopeAddButton({
  resourceIds,
  resourceType,
  checkId,
  provider,
  connectionId,
  fixedKind,
  presetReason,
  presetJustification,
  existing,
  variant = 'button',
  bulk = false,
  triggerLabel,
  justificationPlaceholder = 'Why is this resource out of scope? Auditors will see this.',
  onMutated,
  onBulkSuccess,
}: {
  resourceIds: string[]
  resourceType?: string | null
  checkId: string
  provider?: string
  connectionId?: string
  fixedKind?: ResourceExceptionKind
  presetReason?: ExceptionReason
  presetJustification?: string
  existing?: ComplianceExceptionDTO
  variant?: 'button' | 'inline' | 'edit'
  bulk?: boolean
  triggerLabel?: string
  justificationPlaceholder?: string
  onMutated: () => void
  onBulkSuccess?: () => void
}) {
  const seedKind = fixedKind ?? 'FINDING'
  const seedReason = existing?.reason ?? presetReason ?? defaultReasonForKind(seedKind)
  const seedJustification = existing?.justification ?? presetJustification ?? ''
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<ResourceExceptionKind>(seedKind)
  const [reason, setReason] = useState<ExceptionReason>(seedReason)
  const [justification, setJustification] = useState(seedJustification)
  const expiry = useExceptionExpiry(existing?.expiresAt)
  const resourceScopeMutation = useResourceScopeMutation()
  const checkScopeMutation = useCheckResourceScopeMutation()
  const isPending =
    resourceScopeMutation.isPending || checkScopeMutation.isPending

  const reset = () => {
    setKind(seedKind)
    setReason(seedReason)
    setJustification(seedJustification)
    expiry.reset()
  }

  const submit = () => {
    if (resourceIds.length === 0 || justification.trim().length === 0) return
    const onSuccess = () => {
      toast.success(
        existing
          ? 'Exception updated'
          : kind === 'FINDING'
            ? (bulk ? 'Check exceptions added' : 'Check exception added')
            : (bulk ? 'Resources excluded from all checks' : 'Resource excluded from all checks')
      )
      onMutated()
      onBulkSuccess?.()
      setOpen(false)
      reset()
    }
    const onError = (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to scope resource'
      )
    }
    const common = {
      resourceIds,
      provider,
      ...(connectionId ? { connectionId } : {}),
      resourceType,
      action: 'OUT_OF_SCOPE' as const,
      reason,
      justification: justification.trim(),
      expiresAt: expiry.expiresAt,
    }
    if (kind === 'SCOPE') {
      resourceScopeMutation.mutate(common, { onSuccess, onError })
      return
    }
    checkScopeMutation.mutate(
      { ...common, checkId },
      { onSuccess, onError }
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) reset()
      }}
    >
      <PopoverTrigger asChild>
        {variant === 'inline' ? (
          <Button
            type='button'
            variant='link'
            className='h-auto p-0 text-[11px]'
          >
            Mark as exception
          </Button>
        ) : variant === 'edit' ? (
          <Button
            type='button'
            aria-label='Edit exception'
            title='Edit exception'
            variant='outline'
            className='h-7 w-7 p-0'
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        ) : (
          <Button
            type='button'
            variant='outline'
            className='h-7 gap-1 px-2 text-[11px]'
          >
            {triggerLabel ?? (seedKind === 'FINDING' ? 'Add check exception' : 'Mark as out of scope')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align='end' className='w-[360px] p-0'>
        <div className='border-b border-border px-4 py-2.5'>
          <div className='font-body text-text-secondary text-[12px] font-semibold'>
            {existing
              ? 'Edit exception'
              : kind === 'FINDING'
                ? 'Add check exception'
                : 'Exclude resource from all checks'}
          </div>
          <div className='font-body text-text-subtle mt-0.5 text-[11px]'>
            {KIND_POPOVER_HINTS[kind]}
          </div>
        </div>
        <div className='space-y-3 px-4 py-3'>
          <ExceptionKindSelect
            value={kind}
            onChange={(nextKind) => {
              setKind(nextKind)
              setReason(defaultReasonForKind(nextKind, presetReason))
            }}
            disabled={!!fixedKind}
          />
          <div className='space-y-1.5'>
            <Label className='font-body text-text-body text-[11px]'>Reason</Label>
            <Select
              value={reason}
              onValueChange={(value) => setReason(value as ExceptionReason)}
            >
              <SelectTrigger fullWidth className='h-8 text-[12px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(kind === 'SCOPE' ? SCOPE_REASONS : FINDING_REASONS).map(
                  (key) => (
                    <SelectItem key={key} value={key} className='text-[12px]'>
                      {REASON_LABELS[key]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label className='font-body text-text-body text-[11px]'>
              Justification
            </Label>
            <Textarea
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              placeholder={justificationPlaceholder}
              rows={3}
              className='text-[12px]'
            />
          </div>
          <ExceptionExpiryField
            value={expiry.value}
            onChange={expiry.setValue}
          />
          <div className='flex items-center justify-end gap-2 pt-1'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type='button'
              size='sm'
              disabled={justification.trim().length === 0 || isPending}
              onClick={submit}
            >
              {isPending && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
              {existing
                ? 'Save changes'
                : 'Save exception'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function ResourceScopeRemoveButton({
  resourceIds,
  checkId,
  provider,
  connectionId,
  level,
  label,
  onMutated,
}: {
  resourceIds: string[]
  checkId?: string
  provider?: string
  connectionId?: string
  level: 'RESOURCE' | 'CHECK'
  label?: string
  onMutated: () => void
}) {
  const resourceScopeMutation = useResourceScopeMutation()
  const checkScopeMutation = useCheckResourceScopeMutation()
  const isPending =
    resourceScopeMutation.isPending || checkScopeMutation.isPending
  const remove = () => {
    const onSuccess = () => {
      toast.success(
        level === 'RESOURCE' ? SCOPE_REMOVED_TOAST : 'Resource returned to scope'
      )
      onMutated()
    }
    const onError = (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to return resource to scope'
      )
    }
    if (level === 'RESOURCE') {
      resourceScopeMutation.mutate(
        { resourceIds, provider, ...(connectionId ? { connectionId } : {}), action: 'IN_SCOPE' },
        { onSuccess, onError }
      )
      return
    }
    if (!checkId) return
    checkScopeMutation.mutate(
      { resourceIds, provider, ...(connectionId ? { connectionId } : {}), checkId, action: 'IN_SCOPE' },
      { onSuccess, onError }
    )
  }

  return (
    <Button
      type='button'
      disabled={isPending}
      title={level === 'RESOURCE' ? SCOPE_REMOVE_TITLE : label}
      onClick={remove}
      variant='outline'
      className='h-7 gap-1 px-2 text-[11px]'
    >
      {isPending && <Loader2 className='h-3 w-3 animate-spin' />}
      {label ?? (level === 'RESOURCE' ? SCOPE_REMOVE_LABEL : 'Mark in scope')}
    </Button>
  )
}
